import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTenancy } from '@/contexts/TenancyContext';
import { useMSGReadOnly } from '../../contexts/ReadOnlyContext';
import { db } from '@/js/external-js/config/main_config';
import { format } from 'date-fns';
import { Plus, Pencil, Trash2, Save, X } from 'lucide-react';

import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

import EditableField from '@/components/ui/EditableField';
import { FrameworkBadge } from '@/app/pages/msg/components/FrameworkBadge';

import {
  getStandardName,
  fetchRelatedDocuments,
  populateStandardsList,
  populateCustomContent,
  fetchPageContent,
  getAllModules,
  fetchPageContentMDX
} from '@/app/pages/msg/utils/common';
import { MarkdownRenderer } from '@/app/pages/msg/components/MarkdownRenderer';
import { useMarkdownComponents } from '@/app/pages/msg/components/MDXFields';
import RelatedClausesAccordianItem from '@/app/pages/msg/components/RelatedClauses';
import legalObligationsService, { LegalObligation } from '@/services/legalObligationsService';
import { getAuthUID } from '@/lib/authState';
import { SingleUserCombobox } from '@/components/ui/SingleUserCombobox';
import FootNote from '../../components/FootNote';
import AdditionalContent from '../../components/AdditionalContent';
import SupportingDocs from '../../components/SupportingDocs';
import { useMSGVersion } from '../../hooks/useMSGVersion';
import SupportingDocsDropdown from '../../components/SupportingDocsDropdown';

export default function LegalObligationsSection() {
  const { activeTenancy, userIdToName } = useTenancy();
  const { isReadOnly } = useMSGReadOnly();
  const { incrementVersion } = useMSGVersion();

  // Legal Obligations State
  const [obligations, setObligations] = useState<LegalObligation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingObligation, setEditingObligation] = useState<LegalObligation | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    requirement: '',
    updateMethod: 'Manual' as 'Manual' | 'Email',
    owner: '',
    dateAdded: format(new Date(), 'yyyy-MM-dd'),
  });

  // MDX Things
  const [pageTemplateMDX, setPageTemplateMDX] = React.useState<string>('');
  const [modulesList, setModulesList] = useState([]);
  const clauseStandards = useRef<Set<string>>(new Set());
  const [pageClauses, setPageClauses] = useState<Set<string>>(new Set());

  // Function to update pageClauses
  const updatePageClauses = (newClauses: Set<string>) => {
    setPageClauses(new Set(newClauses));
  };

  const [pageTemplate, setPageTemplate] = React.useState<string>('');
  const [supportingDocs, setSupportingDocs] = React.useState([]);
  const [sortedFrameworks, setSortedFrameworks] = React.useState([]);
  const [dynamicStandardNames, setDynamicStandardNames] = useState<Map<string, string>>(new Map());
  const [dynamicContent, setDynamicContent] = React.useState<string>("");

  const pageName = 'context';
  const contentPath = ['legalObligations'];
  const pageRoute = '/MSG/context/legalObligations';

  // Load legal obligations
  const loadObligations = async () => {
    if (!activeTenancy.id) return;

    try {
      setIsLoading(true);
      const data = await legalObligationsService.getAllLegalObligations();
      setObligations(data);
    } catch (error) {
      console.error('Error loading legal obligations:', error);
      toast.error('Failed to load legal obligations');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle owner selection from SingleUserCombobox
  const handleOwnerSelect = (userId: string) => {
    setFormData(prev => ({
      ...prev,
      owner: userId
    }));
  };

  // Handle select changes
  const handleSelectChange = (name: string, value: 'Manual' | 'Email') => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Open dialog for adding a new obligation
  const handleAddClick = () => {
    setEditingObligation(null);
    setFormData({
      requirement: '',
      updateMethod: 'Manual',
      owner: activeTenancy.name || '',
      dateAdded: format(new Date(), 'yyyy-MM-dd'),
    });
    setIsDialogOpen(true);
  };

  // Open dialog for editing an existing obligation
  const handleEditClick = (obligation: LegalObligation) => {
    setEditingObligation(obligation);
    setFormData({
      requirement: obligation.requirement,
      updateMethod: obligation.updateMethod,
      owner: obligation.owner,
      dateAdded: obligation.dateAdded instanceof Date
        ? format(obligation.dateAdded, 'yyyy-MM-dd')
        : obligation.dateAdded,
    });
    setIsDialogOpen(true);
  };

  // Deep-link: open obligation edit when /msg/context/legal/:id
  const { id: routeObligationId } = useParams<{ id?: string }>();
  useEffect(() => {
    const rid = String(routeObligationId || '').trim();
    if (!rid || !Array.isArray(obligations) || obligations.length === 0) return;
    const match = obligations.find(o => String(o.id) === rid);
    if (match) handleEditClick(match);
  }, [routeObligationId, obligations]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeTenancy.id) return;

    setIsSubmitting(true);

    try {
      const obligationData = {
        requirement: formData.requirement,
        updateMethod: formData.updateMethod as 'Manual' | 'Email',
        owner: formData.owner,
      };

      if (editingObligation) {
        const updateData = {
          ...obligationData,
          dateAdded: new Date(formData.dateAdded),
          dateUpdated: new Date(),
          updatedBy: getAuthUID() || 'System'
        };

        await legalObligationsService.updateLegalObligation(
          editingObligation.id!,
          updateData
        );
        await incrementVersion(
          'legal_obligation_update',
          `Updated legal obligation for ${editingObligation.id}`,
          {
            obligationId: editingObligation.id,
            oldData: editingObligation,
            newData: updateData
          }
        );
        toast.success('Legal obligation updated successfully');
      } else {
        const createData = {
          ...obligationData,
          dateAdded: new Date(formData.dateAdded),
          dateUpdated: new Date(),
          owner: getAuthUID() || 'System',
          addedBy: getAuthUID() || 'System',
          updatedBy: getAuthUID() || 'System'
        };

        await legalObligationsService.createLegalObligation(createData
        );
        await incrementVersion(
          'legal_obligation_create',
          `Created legal obligation for ${createData.id}`,
          {
            obligationId: createData.id,
            newData: createData
          }
        );
        toast.success('Legal obligation added successfully');
      }

      await loadObligations();
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Error saving legal obligation:', error);
      toast.error('Failed to save legal obligation');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete action
  const handleDelete = async (id: string) => {
    if (!activeTenancy.id) return;

    if (confirm('Are you sure you want to delete this legal obligation?')) {
      try {
        await legalObligationsService.deleteLegalObligation(id);
        await loadObligations();
        await incrementVersion(
          'legal_obligation_delete',
          `Deleted legal obligation for ${id}`,
          {
            obligationId: id
          }
        );
        toast.success('Legal obligation deleted successfully');
      } catch (error) {
        console.error('Error deleting legal obligation:', error);
        toast.error('Failed to delete legal obligation');
      }
    }
  };

  useEffect(() => {
    if (activeTenancy.id == "") return;

    const loadModules = async () => {
      const modules = await getAllModules();
      const storedVisibilities = JSON.parse(localStorage.getItem('modulesVisibility') || '{}');

      const modulesWithVisibility = modules.map(module => ({
        ...module,
        visible: storedVisibilities[module.id] ?? false
      }));

      setModulesList(modulesWithVisibility);
    }

    const getPageContentMDX = fetchPageContentMDX(db, pageName, 'legalObligations_MDX', activeTenancy, setPageTemplateMDX);
    const getRelatedDocuments = fetchRelatedDocuments(db, activeTenancy.id, pageRoute, setSupportingDocs);
    const getCustomContent = populateCustomContent(db, activeTenancy.id, pageRoute, setDynamicContent);

    // Load obligations when component mounts
    loadObligations();
    loadModules();

    return () => {
      getPageContentMDX();
      getCustomContent();
      getRelatedDocuments();
    };
  }, [activeTenancy]);

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-xl border border-border shadow-sm px-6 py-6 relative">
        {/* Overview Section */}
        <div className="absolute right-4 top-4 z-10">
          <div className="flex items-center space-x-2">
            <SupportingDocsDropdown pageRoute={pageRoute} clauseStandards={clauseStandards} pageClauses={pageClauses} />
            <RelatedClausesAccordianItem modulesList={modulesList} clauseStandards={clauseStandards} setModulesList={setModulesList} />
          </div>
        </div>
        <div className="px-6 py-6">
          <MarkdownRenderer source={pageTemplateMDX} components={useMarkdownComponents(modulesList, clauseStandards, { current: pageClauses }, updatePageClauses)} />
        </div>
        <div style={{ maxWidth: '56rem', marginLeft: 'auto', marginRight: 'auto', padding: '0px 1rem 1.5rem', transition: 'color 0.2s, background-color 0.2s', backgroundColor: 'transparent' }}>
          <AdditionalContent pageRoute={pageRoute} />
          <FootNote />
        </div>
      </div>

      <div className="bg-card rounded-xl border border-border shadow-sm px-6 py-6 mt-4">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-foreground">Obligations</h3>
          {!isReadOnly && (
            <Button onClick={handleAddClick}>
              <Plus className="mr-2 h-4 w-4" /> Add Obligation
            </Button>
          )}
        </div>

        {/* Legal Obligations Table */}
        <div className="rounded-md">
          <Table className="w-full table">
            <TableHeader>
              <TableRow>
                <TableHead>Requirement</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Update Method</TableHead>
                <TableHead>Date Added</TableHead>
                <TableHead>Last Updated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-4">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : obligations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-4 text-muted-foreground">
                    No legal obligations found. Add one to get started.
                  </TableCell>
                </TableRow>
              ) : (
                obligations.map((obligation) => (
                  <TableRow key={obligation.id}>
                    <TableCell className="font-medium">{obligation.requirement}</TableCell>
                    <TableCell>{userIdToName[obligation.owner]}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {obligation.updateMethod}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {obligation.dateAdded
                        ? format(new Date(obligation.dateAdded), 'MMM d, yyyy')
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {obligation.dateUpdated
                        ? format(new Date(obligation.dateUpdated), 'MMM d, yyyy')
                        : 'N/A'}
                    </TableCell>
                    <TableCell className="text-right">
                      {!isReadOnly && (
                        <div className="flex justify-end space-x-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditClick(obligation)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => obligation.id && handleDelete(obligation.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* <div className='bg-card rounded-xl border border-border shadow-sm'>

        <SupportingDocs pageRoute={pageRoute} clauseStandards={clauseStandards} pageClauses={pageClauses} />

      </div> */}
      {/* Add/Edit Legal Obligation Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[600px] bg-secondary">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>
                {editingObligation ? 'Edit Legal Obligation' : 'Add New Legal Obligation'}
              </DialogTitle>
              <DialogDescription>
                {editingObligation
                  ? 'Update the legal obligation details below.'
                  : 'Fill in the details below to add a new legal obligation.'}
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              {/* Date Added */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="dateAdded" className="text-right">
                  Date Added
                </Label>
                <Input
                  id="dateAdded"
                  name="dateAdded"
                  type="date"
                  value={formData.dateAdded}
                  onChange={handleInputChange}
                  className="col-span-3"
                  required
                />
              </div>

              {/* Requirement */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="requirement" className="text-right">
                  Requirement
                </Label>
                <Input
                  id="requirement"
                  name="requirement"
                  value={formData.requirement}
                  onChange={handleInputChange}
                  className="col-span-3"
                  placeholder="Enter requirement"
                  required
                />
              </div>

              {/* Owner */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="owner" className="text-right">
                  Owner
                </Label>
                <SingleUserCombobox
                  selected={formData.owner}
                  setSelected={handleOwnerSelect}
                  className="col-span-3"
                  placeholder="Select Owner"
                />
              </div>

              {/* Update Method */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="updateMethod" className="text-right">
                  Update Method
                </Label>
                <Select
                  value={formData.updateMethod}
                  onValueChange={(value: 'Manual' | 'Email') =>
                    handleSelectChange('updateMethod', value)
                  }
                >
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Select update method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Manual">Manual</SelectItem>
                    <SelectItem value="Email">Email</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    {editingObligation ? 'Update' : 'Save'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>

  );
}
